import {  Users, ArrowRightLeft } from 'lucide-react';
import { Button } from './Button';
import { DatePicker } from './DatePicker';
import { LocationAutocomplete } from './LocationAutocomplete';
import { useState } from 'react';

interface HeroSectionProps {
  onSearch: (params: { origen: string; destino: string; fecha: string; pasajeros: string }) => void;
}

export const HeroSection = ({ onSearch }: HeroSectionProps) => {
  const [origen, setOrigen] = useState('');
  const [destino, setDestino] = useState('');
  const [fecha, setFecha] = useState('');
  const [pasajeros, setPasajeros] = useState('1');

  const handleSwap = () => {
    const temp = origen;
    setOrigen(destino);
    setDestino(temp);
  };

  const handleSubmit = () => {
    if (origen && destino && fecha) {
      onSearch({ origen, destino, fecha, pasajeros });
    }
  };

  return (
    <section className="relative min-h-[480px] lg:h-[700px] flex items-start lg:items-center justify-center pb-20 lg:pb-0 pt-[70px] lg:pt-0">
      {/* Background - Different for mobile */}
      <div className="absolute inset-0">
        {/* Mobile: Gradient background (sin imagen) */}
        <div 
          className="lg:hidden absolute inset-0 bg-gradient-to-br from-[#FF6A2A] to-[#C2410C]"
        >
        </div>

        {/* Desktop: Image background */}
        <div 
          className="hidden lg:block absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1595361314562-0ca40462f4f5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoaWdod2F5JTIwdHJhdmVsJTIwcm9hZHxlbnwxfHx8fDE3NjgyNDE4MjZ8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral')`
          }}
        >
          <div className="absolute inset-0 bg-[#111827] opacity-40"></div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-4 lg:px-8 w-full">
        <div className="max-w-4xl mx-auto">
          {/* Mobile: Greeting */}
          <div className="lg:hidden text-white mb-2.5">
            <p className="text-sm opacity-90 mb-0.5">Buenos días</p>
            <h2 className="text-lg font-semibold">Usuario</h2>
          </div>

          {/* Hero Text */}
          <h1 className="text-white mb-3.5 text-2xl lg:text-5xl lg:text-center lg:mb-5">
            <span className="lg:hidden">¿Cuál es tu próximo destino?</span>
            <span className="hidden lg:block">Compra tus boletos de autobús en minutos</span>
          </h1>
          <p className="hidden lg:block text-white text-lg lg:text-xl mb-12 opacity-95 drop-shadow-md text-center">
            Elige origen, destino y fecha. Sin filas, sin estrés.
          </p>

          {/* Search Bar */}
          <div className="bg-white rounded-[16px] shadow-2xl p-4 lg:p-10">
            {/* Mobile: Stacked Layout */}
            <div className="lg:hidden space-y-3 mb-4">
              {/* Origen with Autocomplete */}
              <LocationAutocomplete
                value={origen}
                onChange={setOrigen}
                placeholder="Origen"
                showSwapButton={true}
                onSwap={handleSwap}
              />

              {/* Destino with Autocomplete */}
              <LocationAutocomplete
                value={destino}
                onChange={setDestino}
                placeholder="Destino"
              />

              {/* Fecha y Pasajeros en una fila */}
              <div className="grid grid-cols-2 gap-3">
                <DatePicker value={fecha} onChange={setFecha} placeholder="Fecha" />
                <div className="relative">
                  <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <input
                    type="number"
                    value={pasajeros}
                    onChange={(e) => setPasajeros(e.target.value)}
                    min="1"
                    max="9"
                    placeholder="1"
                    className="w-full pl-12 pr-4 py-4 bg-[#F7F8FA] border-none rounded-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] transition-all text-[#111827] font-medium"
                  />
                </div>
              </div>
            </div>

            {/* Desktop: Original Grid Layout */}
            <div className="hidden lg:grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6">
              {/* Origen with Autocomplete */}
              <div className="lg:col-span-4">
                <LocationAutocomplete
                  value={origen}
                  onChange={setOrigen}
                  placeholder="Ciudad de origen"
                  label="Origen"
                />
              </div>

              {/* Swap Button */}
              <div className="lg:col-span-1 flex items-end justify-center pb-1">
                <button
                  onClick={handleSwap}
                  className="w-11 h-11 bg-[#F7F8FA] hover:bg-[#FF6A2A] hover:text-white text-[#6B7280] rounded-[14px] flex items-center justify-center transition-all duration-200 border border-[#E5E7EB]"
                  aria-label="Intercambiar origen y destino"
                >
                  <ArrowRightLeft className="w-5 h-5" />
                </button>
              </div>

              {/* Destino with Autocomplete */}
              <div className="lg:col-span-4">
                <LocationAutocomplete
                  value={destino}
                  onChange={setDestino}
                  placeholder="Ciudad de destino"
                  label="Destino"
                />
              </div>

              {/* Fecha */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-[#111827] mb-2 text-left">
                  Fecha
                </label>
                <DatePicker value={fecha} onChange={setFecha} />
              </div>

              {/* Pasajeros */}
              <div className="lg:col-span-1">
                <label className="block text-sm font-medium text-[#111827] mb-2 text-left">
                  <Users className="w-4 h-4 inline mr-1" />
                </label>
                <input
                  type="number"
                  value={pasajeros}
                  onChange={(e) => setPasajeros(e.target.value)}
                  min="1"
                  max="9"
                  className="w-full px-4 py-3.5 border border-[#E5E7EB] rounded-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent transition-all text-center"
                />
              </div>
            </div>

            {/* CTA Button */}
            <Button 
              variant="primary" 
              className="w-full text-base py-4 lg:py-3.5 lg:w-auto lg:px-12" 
              onClick={handleSubmit}
            >
              Buscar salidas
            </Button>

            {/* Helper Text */}
            <p className="hidden lg:block text-sm text-[#6B7280] mt-5 text-center">
              Fechas en horario de México (CDMX)
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};